import React, { useState } from 'react';
import { Button, Date } from '@ui-modules';
import { message, Modal, Radio, Spin } from 'antd';
import { UseMutationResult, useQuery } from 'react-query';
import { includes, without } from 'lodash';
import moment from 'moment';
import { IProviders, IProvidersResponse, IQuotationPayload } from '@common/interfaces';
import './styles.scss';
import { useRepository } from '@context';

interface IQuotationModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  onButtonClick: (data: { deadline: Date; providers: IProviders[] }) => void;
  mutation: UseMutationResult<any, any, IQuotationPayload>;
}

const PROVIDERS_LIMIT = 100;

const QuotationModal = ({
  isModalOpen,
  setIsModalOpen,
  onButtonClick,
  mutation,
}: IQuotationModalProps) => {
  const [selectedProviders, setSelectedProviders] = useState<IProviders[]>([]);
  const [quotationDate, setQuotationDate] = useState<Date | undefined>(undefined);
  const { freightRepository } = useRepository();
  const facilityId = +localStorage.getItem('facility')!;
  const country = localStorage.getItem('country');

  const { data: providersData } = useQuery<IProvidersResponse>(
    `providers-${facilityId}`,
    () =>
      freightRepository.getProvidersList({
        offset: 0,
        limit: PROVIDERS_LIMIT,
        facilities__country_name: country ?? '',
      }),
    {
      refetchOnWindowFocus: false,
      retry: 2,
      enabled: isModalOpen,
      onError: () => {
        setIsModalOpen(false);
        return message.error('Fetching list of providers has been failed');
      },
    },
  );

  const onProviderClick = (name: IProviders) => {
    if (!includes(selectedProviders, name)) {
      setSelectedProviders([...selectedProviders, name]);
    } else {
      setSelectedProviders(without(selectedProviders, name));
    }
  };

  const providersList = providersData?.results;

  const { isLoading } = mutation;

  return (
    <Modal
      footer={null}
      title={
        <div className="modal-title">
          <p>QUOTATION REQUEST</p>
        </div>
      }
      width="60%"
      centered
      visible={isModalOpen}
      onCancel={() => !isLoading && setIsModalOpen(false)}
    >
      <div className="quotation-select-label">
        <p>{providersList?.length ?? 0} Available providers - clicks to select/unselect</p>
        <p>{selectedProviders?.length} selected</p>
      </div>
      <Radio
        onClick={() =>
          setSelectedProviders(
            providersList?.length !== selectedProviders?.length ? providersList ?? [] : [],
          )
        }
        checked={providersList?.length === selectedProviders?.length}
        className="select-all-radio"
      >
        {providersList?.length !== selectedProviders?.length ? 'Select all' : 'Deselect all'}
      </Radio>
      <Spin spinning={isLoading}>
        <div className="providers-container">
          {providersList?.map((item) => {
            return (
              <button
                type="button"
                key={item.legal_name}
                onClick={() => onProviderClick(item)}
                className={`provider-item ${includes(selectedProviders, item) && 'selected'}`}
              >
                <span>{item.legal_name}</span>
              </button>
            );
          })}
        </div>
      </Spin>
      <div className="quotation-submit-section">
        <div>
          <span className="quotation-datepicker-label">Quotation submission deadline</span>
          <Date
            placeholderText="DD/MM/YYYY"
            dateValue={quotationDate}
            dateFormat="dd/MM/yyyy"
            minDate={moment().toDate()}
            onChange={(value: Date) => {
              setQuotationDate(value);
            }}
          />
        </div>
        <Button
          text={isLoading ? 'Processing' : 'Confirm'}
          className="quotation-button send"
          disabled={isLoading || selectedProviders.length <= 0}
          type="submit"
          onClick={() =>
            onButtonClick({
              deadline: quotationDate as Date,
              providers: selectedProviders,
            })
          }
        />
      </div>
    </Modal>
  );
};

export default QuotationModal;
